import ReactGA from 'react-ga4';
import { toast } from 'sonner';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
//////////////////////////////////////////////////////
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Button,
} from '@/app/shared/components';
import { telegramWebApp } from '@/main';
import { AuthService } from '@/core/services';
import { useCustomDispatch } from '@/core/redux/hooks';
import {
	setCounter,
	setEmail,
	setEmailUpdated,
	setStickerWonDialog,
} from '@/core/redux/slices';

const FormSchema = z.object({
	email: z
		.string()
		.min(2, {
			message: 'Email must be at least 2 characters.',
		})
		.email('This is not a valid email.'),
});

interface FormSyncProps {
	isSubmitted: () => void;
}

export const FormSync: React.FC<FormSyncProps> = ({
	isSubmitted,
}: FormSyncProps) => {
	/*********************
	// #region Hooks
	**********************/
	const { t } = useTranslation();
	const dispatch = useCustomDispatch();

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			email: '',
		},
	});

	const onSubmit = (data: z.infer<typeof FormSchema>) => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');
		const encodedEmail = encodeURIComponent(data.email);

		toast.promise(AuthService.setEmail(encodedEmail), {
			loading: t('general.loading'),
			success: (response: {
				currentCoins: number;
				isAwardWinning: boolean;
			}) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'vottun-world',
					action: 'Sync Vottun World',
					label: `${data.email} syncronized`, // optional
				});

				// Handle response
				dispatch(setEmail({ email: data.email }));
				dispatch(setCounter(response.currentCoins));
				dispatch(setEmailUpdated(true));
				// Check if has a gift
				if (response.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				return t('airdrop.sync.syncSuccess');
			},
			error: (error: any) => {
				if (error.response.data.code === 'ERROR_EMPTY_SQL_RESULT') {
					return t('airdrop.sync.syncError');
				} else {
					return `${error.response.data.message}`;
				}
			},
		});

		isSubmitted();
	};

	return (
		<div className="w-full mx-auto">
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="w-full space-y-6"
				>
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Email</FormLabel>
								<FormControl>
									<Input
										type="email"
										className="bg-customer-800 text-white border-none"
										placeholder="Email"
										{...field}
									/>
								</FormControl>
								<FormDescription>
									{t('airdrop.addEmail')}
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>
					<Button
						variant="primary"
						className="py-6 w-full text-sm rounded-lg"
						type="submit"
					>
						Connect
					</Button>
				</form>
			</Form>
		</div>
	);
};
